import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { BlankSlate, Box, Button } from '@factorialco/deprecated-design-system'
import sortBy from 'lodash/sortBy'

import { observer } from 'mobx-react'

import ListEmployees from 'modules/core/components/ListEmployees'
import Loading from 'modules/core/components/Loading'
import { Panes } from 'modules/core/components/Panes'
import PanesHeader from 'modules/core/components/PanesHeader'
// Hooks
import { useNavigation } from 'modules/core/hooks/useNavigation'
import FiltersForm from 'modules/core/lib/forms/Filters'
import i18n from 'modules/core/lib/i18n'
import { normalizeText } from 'modules/core/lib/stringUtils'
import { employeeUser } from 'modules/core/store/collections/users'

import type { ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import employees from 'modules/employees/store/collections/employees'
import type Employee from 'modules/employees/store/models/Employee'
import { useCurrentEmployeesInPolicy } from 'modules/timeoff/hooks/useCurrentEmployeesInPolicy'
import { hasPolicyTemporality } from 'modules/timeoff/lib/features/TimeoffFeatures'
import { timeoffPolicyActiveEmployees } from 'modules/timeoff/lib/timeoffPolicyEmployees'
import type TimeoffPolicy from 'modules/timeoff/store/models/TimeoffPolicy/Policy'

import Item from './Item/index'

interface Props {
  policy: TimeoffPolicy | undefined
  loadingPolicies: boolean
  contractVersions: ContractVersions
}

const TimeoffPolicyEmployeeList = ({
  policy,
  loadingPolicies,
  children,
  contractVersions,
}: React.PropsWithChildren<Props>) => {
  const { navigation } = useNavigation()

  const filtersForm = React.useRef(new FiltersForm({})).current
  const navigate = useNavigate()

  const activeEmployees: React.MutableRefObject<Employee[]> = React.useRef(
    policy ? timeoffPolicyActiveEmployees(policy, employees) : []
  )
  const {
    loading: loadingGraphql,
    currentPolicyAssignments,
    error,
  } = useCurrentEmployeesInPolicy(policy ? [policy.id] : [])

  const isLoading = loadingPolicies || loadingGraphql

  const policyAssignments: Record<number, (typeof currentPolicyAssignments)[number]> = {}

  currentPolicyAssignments.forEach((assignment) => {
    policyAssignments[assignment.employeeId] = assignment
  })

  activeEmployees.current = employees.filter((employee) =>
    Object.keys(policyAssignments).includes(employee.id.toString())
  )

  const employeesInList = sortBy(activeEmployees.current, (employee: Employee) => {
    return normalizeText(employeeUser(employee).fullName())
  })

  const renderEmployeeListItem = (employee: Employee) =>
    policy ? (
      <Item
        key={employee.id}
        employee={employee}
        policy={policy}
        effectiveAt={hasPolicyTemporality() ? policyAssignments[employee.id]?.effectiveAt : null}
      />
    ) : null

  if (isLoading) {
    return <Loading />
  }
  if (error) {
    return <BlankSlate illustration="alert" title="" description="" />
  }

  return (
    <>
      <Panes direction="vertical">
        <Box paddingTop="s48">
          <PanesHeader
            illustration="group"
            title={i18n.t('common.employees')}
            description={i18n.t('settings.timeoff_policies.employees.description')}
          />
          <Box paddingBottom="s32">
            <Button
              onClick={() =>
                navigate(
                  navigation.settings.timeoffPolicies.id.employees.add.fullPath({
                    id: policy?.attributes.id,
                  })
                )
              }
              iconName="add"
            >
              {i18n.t('settings.timeoff_policies.employees.add_employees')}
            </Button>
          </Box>
          <ListEmployees
            employees={employeesInList}
            filtersForm={filtersForm}
            renderEmployeeListItem={renderEmployeeListItem}
            contractVersions={contractVersions}
          />
        </Box>
      </Panes>
      {children}
    </>
  )
}

export default observer(TimeoffPolicyEmployeeList)
